// Phase C-ui — BatchModal «Сходимость» mode form.
//
// One submit creates one convergence series PER station. The form
// is intentionally simpler than the Backfill form: convergence
// runs are exploratory («посмотрим как точность зависит от длины
// окна»), not «fill missing tasks across the catalogue», so the
// matrix is just stations × configs × lengths × 1 date.
//
// The lengths picker carries the 5 spec-default checkboxes plus a
// «своя длина, ч» input — operators routinely want a 2h or 4h
// cut that's not in the default set.

import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { api, ApiError } from '../api/client';
import type {
  ConvergencePrepAccepted,
  ConvergenceRunRequest,
} from '../api/types';
import { cn } from '../lib/classnames';

/** Phase C-ui-v2 — sub-mode inside «Сходимость». «По кодам»
 *  preserves the v1 textarea flow; «Случайные» switches to a
 *  random_n input + auto-substitute checkbox that reuses the
 *  Phase J registry-first picker on the backend. */
type StationSubMode = 'codes' | 'random';

/** Spec §A.1 — operator-facing default lengths (hours). */
const DEFAULT_LENGTH_OPTIONS: readonly number[] = [
  24, 12, 6, 3, 1.5,
] as const;

/** Mirror of BatchModal's gnss-config catalogue — keep in sync.
 *  solo + all4 in the first row; other 11 combos behind «ещё». */
const DEFAULT_GNSS_CONFIGS: readonly string[] = [
  'gps',
  'glo',
  'gal',
  'bds',
  'all4',
] as const;
const EXTRA_GNSS_CONFIGS: readonly string[] = [
  'gps+glo',
  'gps+gal',
  'gps+bds',
  'glo+gal',
  'glo+bds',
  'gal+bds',
  'gps+glo+gal',
  'gps+glo+bds',
  'gps+gal+bds',
  'glo+gal+bds',
] as const;

function todayMinusOne(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Parse user input into a deduped, in-range length list. Returns
 *  the empty array on «no valid numbers» so the parent can disable
 *  the Submit button. */
function parseCustomLength(raw: string): number | null {
  if (!raw.trim()) return null;
  const n = Number(raw.replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0 || n > 24) return null;
  return n;
}

export function ConvergenceModeForm({
  onAccepted,
}: {
  /** Wave 4b — called once the async endpoint returns 202 and the
   *  «принято, готовлю N» panel is shown. The parent registers the
   *  batch for background polling and (on «Готово») closes the modal. */
  onAccepted: (resp: ConvergencePrepAccepted) => void;
}) {
  const [subMode, setSubMode] = useState<StationSubMode>('codes');
  const [stationsRaw, setStationsRaw] = useState<string>('');
  const [randomNRaw, setRandomNRaw] = useState<string>('5');
  const [autoSubstitute, setAutoSubstitute] = useState<boolean>(true);
  const [obsDate, setObsDate] = useState<string>(todayMinusOne());
  const [gnssConfigs, setGnssConfigs] = useState<string[]>(['gps']);
  const [showExtra, setShowExtra] = useState(false);
  const [selectedLengths, setSelectedLengths] = useState<Set<number>>(
    new Set(DEFAULT_LENGTH_OPTIONS),
  );
  const [customLengthRaw, setCustomLengthRaw] = useState<string>('');

  const stationsList = useMemo(
    () =>
      stationsRaw
        .split(/[\s,]+/)
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean),
    [stationsRaw],
  );

  // Defect fix 2026-06-14 — keep the cap (the backend enforces
  // `random_n` ≤ RANDOM_CAP; a larger value would 422), but surface it
  // HONESTLY instead of silently clamping. `randomNRequested` is what the
  // operator typed; `randomN` is what we actually send; `randomCapped` drives
  // the «запрошено N, обрабатываю CAP (лимит)» notice. N ≤ CAP → no notice, no
  // behaviour change. Before this, an operator typing over the cap lost
  // stations with zero feedback. 2026-06-15: cap raised 50→100 (quiet-window
  // deploy; backend ConvergenceRunRequest le=100 kept in sync).
  const RANDOM_CAP = 100;
  const randomNRequested = useMemo(() => {
    const n = Number(randomNRaw);
    if (!Number.isFinite(n) || n < 1) return 0;
    return Math.floor(n);
  }, [randomNRaw]);
  const randomN = useMemo(
    () => Math.min(RANDOM_CAP, randomNRequested),
    [randomNRequested],
  );
  const randomCapped = subMode === 'random' && randomNRequested > RANDOM_CAP;

  // Phase C-ui-v2.1 — surface Phase J's pre-flight filtering in the
  // random sub-mode so the operator sees «N доступно, M закешированных
  // недоступных» (mirror of the regular Случайные form). The
  // ITRF2014 hard-code matches the convergence handler's default;
  // changing this would require threading the operator-chosen model
  // through both the form and the query key. Cache 60s — operator
  // typically commits faster than a background sweep would change
  // the cache. Disabled outside the random sub-mode so we don't pay
  // for a request the operator never sees.
  const poolQuery = useQuery({
    queryKey: ['conv-pool-availability', obsDate, 'ITRF2014'],
    queryFn: () =>
      api.getPoolAvailability({
        obs_dates: [obsDate],
        tectonic_model: 'ITRF2014',
      }),
    enabled: subMode === 'random' && obsDate.length === 10,
    staleTime: 60_000,
  });

  // Effective station count for the «jobs = N × M × K» summary —
  // depends on the sub-mode the operator is currently looking at.
  const stationCount = subMode === 'codes' ? stationsList.length : randomN;

  const customLength = useMemo(
    () => parseCustomLength(customLengthRaw),
    [customLengthRaw],
  );

  const finalLengths = useMemo(() => {
    const set = new Set<number>(selectedLengths);
    if (customLength !== null) set.add(customLength);
    return Array.from(set).sort((a, b) => b - a);
  }, [selectedLengths, customLength]);

  const totalJobs = stationCount * gnssConfigs.length * finalLengths.length;

  // Wave 4b — async create. The mutation resolves with the 202 body
  // (batch_id + viable_count + note/skipped); we DON'T close the modal
  // on success — instead the «принято, готовлю N» panel below replaces
  // the form so the operator reads the note and which stations were
  // skipped (honesty at input) before dismissing. ``runMut.data`` holds
  // the accepted response for that panel.
  const runMut = useMutation({
    mutationFn: (payload: ConvergenceRunRequest) =>
      api.runConvergenceAsync(payload),
  });
  const accepted = runMut.data;

  const canSubmit =
    stationCount > 0 &&
    gnssConfigs.length > 0 &&
    finalLengths.length > 0 &&
    !runMut.isPending;

  const onSubmit = () => {
    const base = {
      obs_date: obsDate,
      gnss_configs: gnssConfigs,
      lengths_hours: finalLengths,
      tectonic_model: 'ITRF2014',
      reduction_date: '2011-01-01',
    };
    const payload: ConvergenceRunRequest =
      subMode === 'codes'
        ? { ...base, stations: stationsList }
        : { ...base, random_n: randomN, auto_substitute: autoSubstitute };
    runMut.mutate(payload);
  };

  const toggleLength = (h: number) =>
    setSelectedLengths((cur) => {
      const next = new Set(cur);
      if (next.has(h)) next.delete(h);
      else next.add(h);
      return next;
    });

  const apiError = runMut.error;
  const errorMsg =
    apiError instanceof ApiError
      ? `Ошибка ${apiError.status}: ${apiError.message}`
      : apiError instanceof Error
        ? apiError.message
        : null;

  // Wave 4b — accepted panel. Shown after the 202 instead of closing
  // the modal, so the operator sees the note + which stations were
  // skipped (and why) before dismissing. «Готово» hands control back to
  // the parent, which closes the modal; the «готовлю N/M» card in the
  // Feed takes over from there.
  if (accepted) {
    const n = accepted.total;
    const seriesWord = n === 1 ? 'серию' : n < 5 ? 'серии' : 'серий';
    return (
      <>
        <div
          className="modal-info"
          role="status"
          data-testid="conv-accepted"
          style={{ borderColor: 'var(--accent-success, #2e7d32)' }}
        >
          ✓ Принято. Готовлю {n} {seriesWord} в фоне — прогресс появится
          карточкой «Готовлю серии…» в ленте.
        </div>
        {(accepted.requested_count ?? 0) > 0 && (
          <div
            className="modal-info"
            role="note"
            data-testid="conv-accepted-funnel"
          >
            <strong>Разбор отбора станций:</strong>
            <div className="conv-funnel" style={{ marginTop: 4 }}>
              <div>
                Запрошено:{' '}
                {randomCapped
                  ? `${randomNRequested} → ${RANDOM_CAP} (лимит за заход)`
                  : accepted.requested_count}
              </div>
              {(accepted.excluded_unavailable_count ?? 0) > 0 && (
                <div>
                  Исключено по доступности на дату:{' '}
                  {accepted.excluded_unavailable_count}
                </div>
              )}
              {accepted.skipped_unavailable_stations.length > 0 && (
                <div>
                  Без эталона SINEX (±5 дней):{' '}
                  {accepted.skipped_unavailable_stations.length}
                </div>
              )}
              <div>Принято в работу: {accepted.total}</div>
            </div>
          </div>
        )}
        {accepted.note && (
          <div className="modal-info" role="note" data-testid="conv-accepted-note">
            ℹ️ {accepted.note}
          </div>
        )}
        {accepted.skipped_unavailable_stations.length > 0 && (
          <div
            className="modal-info"
            role="note"
            data-testid="conv-accepted-skipped"
          >
            Пропущены (нет данных в окне ±5 дней):{' '}
            {accepted.skipped_unavailable_stations.join(', ')}
          </div>
        )}
        <div className="modal-actions">
          <button
            className="primary"
            onClick={() => onAccepted(accepted)}
            type="button"
            data-testid="conv-accepted-done"
          >
            Готово
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="modal-field">
        <label>Станции</label>
        <div
          className="mode-toggle"
          role="tablist"
          data-testid="conv-station-submode-toggle"
        >
          <button
            type="button"
            role="tab"
            aria-selected={subMode === 'codes'}
            className={cn(subMode === 'codes' && 'active')}
            onClick={() => setSubMode('codes')}
            data-testid="conv-submode-codes"
          >
            по кодам
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={subMode === 'random'}
            className={cn(subMode === 'random' && 'active')}
            onClick={() => setSubMode('random')}
            data-testid="conv-submode-random"
          >
            случайные
          </button>
        </div>
        {subMode === 'codes' ? (
          <>
            <textarea
              id="conv-stations"
              rows={2}
              value={stationsRaw}
              onChange={(e) => setStationsRaw(e.target.value)}
              placeholder="YAR2 DAV1 KIRU"
              data-testid="conv-stations"
              style={{ marginTop: 8 }}
            />
            <span className="hint">
              каждая станция = своя серия кусков · {stationsList.length} шт.
            </span>
          </>
        ) : (
          <>
            <div
              className="modal-info"
              role="note"
              style={{ marginTop: 8 }}
            >
              ℹ️ В режиме случайной выборки часть станций может
              оказаться временно недоступной. Подключено 7 IGS
              data-центров.
            </div>
            {poolQuery.data && (
              <div
                className="modal-info modal-pool-counter"
                role="note"
                data-testid="conv-pool-availability"
              >
                📊 Доступно: {poolQuery.data.available} (исключено{' '}
                {poolQuery.data.excluded} как закешированных
                недоступными)
              </div>
            )}
            <div className="modal-field-inline" style={{ marginTop: 8 }}>
              <label htmlFor="conv-random-n">сколько станций:</label>
              <input
                id="conv-random-n"
                type="number"
                min={1}
                max={500}
                value={randomNRaw}
                onChange={(e) => setRandomNRaw(e.target.value)}
                data-testid="conv-random-n"
                className="gnss-qa-input num-input-small"
              />
            </div>
            {randomCapped && (
              <div
                className="modal-info"
                role="note"
                data-testid="conv-random-cap-note"
                style={{ borderColor: 'var(--accent-warning, #b26a00)' }}
              >
                ⚠️ Запрошено {randomNRequested}, обрабатываю {RANDOM_CAP} (лимит
                за один заход). Остальные можно создать следующим заходом.
              </div>
            )}
            <label
              className="checkbox-row"
              style={{ marginTop: 8 }}
              data-testid="conv-auto-substitute-row"
            >
              <input
                type="checkbox"
                checked={autoSubstitute}
                onChange={(e) => setAutoSubstitute(e.target.checked)}
                className="gnss-qa-checkbox"
                data-testid="conv-auto-substitute"
              />
              <span>Автозамена недоступных станций (Phase J)</span>
            </label>
            <span className="hint">
              {randomN} случайных станц
              {randomN === 1 ? 'ия' : randomN < 5 ? 'ии' : 'ий'} из
              активного IGS-пула · каждая = своя серия кусков
            </span>
          </>
        )}
      </div>

      <div className="modal-field">
        <label htmlFor="conv-date">Дата наблюдений</label>
        <input
          id="conv-date"
          type="date"
          value={obsDate}
          onChange={(e) => setObsDate(e.target.value)}
          data-testid="conv-date"
          className="gnss-qa-date"
        />
      </div>

      <div className="modal-field">
        <label>GNSS конфигурации</label>
        <div className="gnss-chip-row">
          {DEFAULT_GNSS_CONFIGS.map((cfg) => {
            const active = gnssConfigs.includes(cfg);
            return (
              <button
                key={cfg}
                type="button"
                className={cn(active && 'active')}
                onClick={() =>
                  setGnssConfigs((cur) =>
                    cur.includes(cfg)
                      ? cur.filter((c) => c !== cfg)
                      : [...cur, cfg],
                  )
                }
                data-testid={`conv-cfg-${cfg}`}
              >
                {cfg}
              </button>
            );
          })}
          <button
            type="button"
            className="more-toggle"
            onClick={() => setShowExtra((v) => !v)}
          >
            {showExtra ? 'свернуть' : 'ещё ▾'}
          </button>
          {showExtra &&
            EXTRA_GNSS_CONFIGS.map((cfg) => {
              const active = gnssConfigs.includes(cfg);
              return (
                <button
                  key={cfg}
                  type="button"
                  className={cn(active && 'active')}
                  onClick={() =>
                    setGnssConfigs((cur) =>
                      cur.includes(cfg)
                        ? cur.filter((c) => c !== cfg)
                        : [...cur, cfg],
                    )
                  }
                >
                  {cfg}
                </button>
              );
            })}
        </div>
        <span className="hint">{gnssConfigs.length} выбрано</span>
      </div>

      <div className="modal-field">
        <label>Длины наблюдений (часы)</label>
        <div className="gnss-chip-row" data-testid="conv-lengths">
          {DEFAULT_LENGTH_OPTIONS.map((h) => {
            const active = selectedLengths.has(h);
            return (
              <button
                key={h}
                type="button"
                className={cn(active && 'active')}
                onClick={() => toggleLength(h)}
                data-testid={`conv-len-${h}`}
              >
                {h}ч
              </button>
            );
          })}
        </div>
        <div className="modal-field-inline" style={{ marginTop: 8 }}>
          <label htmlFor="conv-custom-length">+ своя длина, ч:</label>
          <input
            id="conv-custom-length"
            type="text"
            inputMode="decimal"
            value={customLengthRaw}
            onChange={(e) => setCustomLengthRaw(e.target.value)}
            placeholder="напр. 2"
            data-testid="conv-custom-length"
            className="gnss-qa-input num-input-small"
          />
          {customLengthRaw && customLength === null && (
            <span className="hint" style={{ color: 'var(--accent-error)' }}>
              0 &lt; ч ≤ 24
            </span>
          )}
        </div>
        <span className="hint">
          {finalLengths.length === 0
            ? 'выбери хотя бы одну длину'
            : `длин: ${finalLengths.map((h) => `${h}ч`).join(', ')}`}
        </span>
      </div>

      <div className="modal-info" role="note" data-testid="conv-summary">
        jobs = станции × конфиги × длины = {stationCount} ×{' '}
        {gnssConfigs.length} × {finalLengths.length} ={' '}
        <b>{totalJobs}</b>
      </div>

      {errorMsg && <div className="modal-error">{errorMsg}</div>}

      <div className="modal-actions">
        <button
          className="primary"
          onClick={onSubmit}
          disabled={!canSubmit}
          type="button"
          data-testid="conv-submit"
        >
          {runMut.isPending ? 'Создаю…' : 'Запустить сходимость'}
        </button>
      </div>
    </>
  );
}
